import { InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { atom, useAtom } from "jotai";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getUserExternalTransactions, getUserOrders } from "api/methods";
import { GetResult, Models } from "api/types";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { EmptyState } from "molecules/empty-state/EmptyState";
import { HistoryListEntryProps } from "molecules/history-list-entry/HistoryListEntry";
import TopNav from "molecules/navbars/TopNav";
import { HistoryList } from "organisms/history-list/HistoryList";
import { notNull } from "toolbox/Guards";
import { formatCurrency } from "toolbox/Money";
import { AccountDestinationSkeleton } from "../AccountDestinationSkeleton";

import { infiniteQueryKey } from "api/queryKeys";

export type ExternalTransaction = Models<"ExternalTransactionDetails">;

export const isAdjustment = (tx: ExternalTransaction) => tx.type === "Adjustment";

export const getExternalTransactions = getUserExternalTransactions;

const countAtom = atom(1);

const formatHistoryData = (
  orderData: InfiniteData<GetResult<"/user/orders">>,
  externalTransactions: ExternalTransaction[],
  t: ReturnType<typeof useTranslation>["t"],
  onOrderClick: (id: string) => void,
  onExternalClick: (id: string) => void,
) => {
  const handleOrderClick = (id: string) => () => onOrderClick(id);
  const handleExternalClick = (id: string) => () => onExternalClick(id);

  const grouped: Record<string, HistoryListEntryProps[]> = {};

  const orderValues = orderData.pages?.flatMap((page) => page.items).filter(notNull) ?? [];

  for (const item of orderValues) {
    const date = dayjs(item.date).format("YYYY-MM");
    if (!(date in grouped)) grouped[date] = [];

    grouped[date].push({
      id: item.id,
      title: item.isMembershipRenewal
        ? t("orderHistoryPage.membershipRenewal", { membershipName: item.membershipName })
        : t("orderHistoryPage.orderNumber", { number: item.orderNumber }),
      date: item.date,
      subtitle: `${formatCurrency(item.amount)}`,
      refunded: item.isRefunded,
      onClick: item.isMembershipRenewal ? undefined : handleOrderClick(item.id),
    });
  }

  for (const tx of externalTransactions) {
    const date = dayjs(tx.transactionDate).format("YYYY-MM");
    if (!(date in grouped)) grouped[date] = [];

    const adjustment = isAdjustment(tx);
    const formatted = formatCurrency(Math.abs(tx.amount));
    const subtitle = tx.amount < 0 ? `−${formatted}` : formatted;

    grouped[date].push({
      id: tx.id,
      title: adjustment
        ? t("orderHistoryPage.adjustment")
        : t("orderHistoryPage.orderNumber", { number: tx.externalReferenceId ?? tx.id }),
      badge: t("orderHistoryPage.externalBadge"),
      date: tx.transactionDate,
      subtitle,
      onClick: handleExternalClick(tx.id),
    });
  }

  for (const date of Object.keys(grouped)) {
    grouped[date].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }

  return Object.keys(grouped)
    .sort((a, b) => b.localeCompare(a))
    .map((date) => ({ date, items: grouped[date] }));
};

export const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [count, setCount] = useAtom(countAtom);

  const { fetchNextPage, data, hasNextPage } = useInfiniteQuery({
    queryKey: infiniteQueryKey(getUserOrders.key({})),
    queryFn: ({ pageParam = 1 }) => getUserOrders({ page: pageParam }),
    getNextPageParam: ({ page, totalPages }) => (page < totalPages ? page + 1 : undefined),
    onSuccess: (data) => {
      if (data.pages.length < count) {
        fetchNextPage();
      }
    },
  });

  const { data: externalTransactions } = useQuery({ ...getExternalTransactions.keyed() });

  if (!data) return <AccountDestinationSkeleton title={t("orderHistoryPage.header")} />;

  const hasOrders = data.pages[0].count > 0;
  const hasExternalTransactions = (externalTransactions?.length ?? 0) > 0;
  const isDataAvailable = hasOrders || hasExternalTransactions;

  return (
    <div className="relative flex h-full flex-col bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">{t("orderHistoryPage.header")}</div>
      </TopNav>

      <div className="flex flex-col text-center">
        {!isDataAvailable && (
          <EmptyState
            title={t("orderHistoryPage.emptyTitle")}
            body={t("orderHistoryPage.emptyBody")}
            buttonText={t("orderHistoryPage.browseShop")}
            onClick={() => navigate("/shop")}
          />
        )}
        <HistoryList
          historyListItems={formatHistoryData(
            data,
            externalTransactions ?? [],
            t,
            (id) => navigate(`/account/orders/${id}`),
            (id) => navigate(`/account/external-orders/${id}`),
          )}
          showLoadMoreResultsButton={hasNextPage}
          onLoadMoreClick={() => {
            fetchNextPage();
            setCount(data.pages.length + 1);
          }}
        />
      </div>
      <div className="grow" />
      <div className="flex w-full scroll-py-4 flex-col items-center">
        <RepeatMDFooter />
      </div>
    </div>
  );
};
